import { formatDate } from '@/lib/utils'
import { useLinkedPatientsQuery } from '@/services/linked-patients'
import { borderRadius, colors, sxPresets } from '@/theme'
import { Box, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import CollapsibleCard from './CollapsibleCard'

function formatDateOfBirth(dateOfBirth: string | null): string {
  if (!dateOfBirth) return '-'

  try {
    return formatDate(dateOfBirth)
  } catch {
    return dateOfBirth
  }
}

interface LinkedPatientsSectionProps {
  patientHash: string
  linkedPatientHash: string | null | undefined
  sectionId?: string
}

export default function LinkedPatientsSection({
  patientHash,
  linkedPatientHash,
  sectionId = 'linked-patients'
}: LinkedPatientsSectionProps) {
  const { data: linkedPatients } = useLinkedPatientsQuery(linkedPatientHash, patientHash)

  if (!linkedPatients?.length) return null

  return (
    <CollapsibleCard sectionId={sectionId} title='Linked patients'>
      <Box sx={sxPresets().section.content}>
        <TableContainer
          sx={{
            bgcolor: colors.background.paper,
            border: `1px solid ${colors.border.default}`,
            borderRadius: borderRadius.lg,
            overflow: 'hidden'
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: colors.brand.primaryLight }}>
                <TableCell sx={{ fontWeight: 600 }}>Patient name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Gender</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date of birth</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Post code</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {linkedPatients.map(linked => (
                <TableRow key={linked.hash} sx={{ '&:last-child td': { border: 0 } }}>
                  <TableCell>
                    <Link
                      component={RouterLink}
                      to={`/patients/${linked.hash}`}
                      sx={{ color: colors.brand.primary, fontWeight: 500 }}
                    >
                      {`${linked.firstName} ${linked.lastName}`.trim() || 'Unknown'}
                    </Link>
                  </TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{linked.gender || '-'}</TableCell>
                  <TableCell>{formatDateOfBirth(linked.dateOfBirth)}</TableCell>
                  <TableCell>{linked.postcode || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </CollapsibleCard>
  )
}
